import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Makes the URL query string the source of truth for a table's filters,
 * sort, and pagination, namespaced so multiple tables can share one page
 * without their params colliding (e.g. namespace="members" -> members_page).
 *
 * `fields` is a declarative spec per param:
 *   { page: { type: 'number', default: 1 },
 *     pageSize: { type: 'number', default: LIMIT, allowed: [10, 15, 20, 50] },
 *     search: { type: 'string', default: '' },
 *     sortField: { type: 'string', default: 'name' },
 *     sortOrder: { type: 'enum', allowed: ['ASC', 'DESC'], default: 'ASC' },
 *     billable: { type: 'enum', allowed: [true, false], default: undefined },
 *     tags: { type: 'array', default: [] },
 *     dateRange: { type: 'dateRange', default: { from: null, to: null } } }
 *
 * Unknown/invalid/stale values (not in `allowed`, wrong type, unparsable)
 * silently fall back to that field's `default` instead of throwing.
 */

const buildKey = (namespace, key) => (namespace ? `${namespace}_${key}` : key);

const parseScalar = (raw, spec) => {
  if (raw === null || raw === undefined || raw === '') {
    return spec?.default;
  }
  switch (spec?.type) {
    case 'number': {
      const num = Number(raw);
      if (Number.isNaN(num)) return spec?.default;
      if (spec?.allowed && !spec?.allowed?.includes(num)) return spec?.default;
      return num;
    }
    case 'boolean': {
      if (raw === 'true') return true;
      if (raw === 'false') return false;
      return spec?.default;
    }
    case 'enum': {
      const allowed = spec?.allowed || [];
      const match = allowed?.find((option) => String(option) === raw);
      return match !== undefined ? match : spec?.default;
    }
    case 'array': {
      const values = raw?.split(',')?.filter(Boolean);
      return values?.length ? values : spec?.default;
    }
    default:
      return raw;
  }
};

const serializeScalar = (value, spec) => {
  if (value === undefined || value === null || value === '') return null;
  if (spec?.type === 'array') {
    return value?.length ? value?.join(',') : null;
  }
  return String(value);
};

export const useTableQueryParams = ({ namespace = '', fields }) => {
  const location = useLocation();
  const history = useHistory();

  const params = useMemo(() => {
    const search = new URLSearchParams(location?.search);
    const result = {};
    Object.keys(fields || {})?.forEach((field) => {
      const spec = fields?.[field];
      if (spec?.type === 'dateRange') {
        const from = search?.get(buildKey(namespace, `${field}From`));
        const to = search?.get(buildKey(namespace, `${field}To`));
        result[field] = {
          from: from || spec?.default?.from || null,
          to: to || spec?.default?.to || null,
        };
        return;
      }
      const raw = search?.get(buildKey(namespace, field));
      result[field] = parseScalar(raw, spec);
    });
    return result;
    // location object identity changes every navigation; only its search
    // string should trigger recomputation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.search, namespace, fields]);

  const setParams = useCallback(
    (partial) => {
      const search = new URLSearchParams(location?.search);
      Object.keys(partial || {})?.forEach((field) => {
        const spec = fields?.[field];
        const value = partial?.[field];
        if (spec?.type === 'dateRange') {
          const fromKey = buildKey(namespace, `${field}From`);
          const toKey = buildKey(namespace, `${field}To`);
          if (value?.from) {
            search?.set(fromKey, value?.from);
          } else {
            search?.delete(fromKey);
          }
          if (value?.to) {
            search?.set(toKey, value?.to);
          } else {
            search?.delete(toKey);
          }
          return;
        }
        const key = buildKey(namespace, field);
        const serialized = serializeScalar(value, spec);
        if (serialized === null) {
          search?.delete(key);
        } else {
          search?.set(key, serialized);
        }
      });
      history?.replace({
        pathname: location?.pathname,
        search: search?.toString(),
      });
    },
    [fields, history, location?.pathname, location?.search, namespace],
  );

  const resetParams = useCallback(() => {
    const search = new URLSearchParams(location?.search);
    Object.keys(fields || {})?.forEach((field) => {
      const spec = fields?.[field];
      if (spec?.type === 'dateRange') {
        search?.delete(buildKey(namespace, `${field}From`));
        search?.delete(buildKey(namespace, `${field}To`));
        return;
      }
      search?.delete(buildKey(namespace, field));
    });
    history?.replace({
      pathname: location?.pathname,
      search: search?.toString(),
    });
  }, [fields, history, location?.pathname, location?.search, namespace]);

  return { params, setParams, resetParams };
};

/** Translates AntD Table's onChange(pagination, filters, sorter) into
 * { page, pageSize, sortField, sortOrder } ready to pass into setParams.
 * `currentPageSize` is the pageSize the table was on before this change;
 * when the user changes the page size, the page resets to 1 instead of
 * keeping AntD's recalculated `current`. */
export const mapAntTableChangeToParams = (
  pagination,
  sorter,
  currentPageSize,
) => {
  const field = sorter?.field || sorter?.columnKey;
  let sortOrder;
  if (sorter?.order === 'descend') sortOrder = 'DESC';
  else if (sorter?.order === 'ascend') sortOrder = 'ASC';
  const pageSizeChanged = pagination?.pageSize !== currentPageSize;
  return {
    page: pageSizeChanged ? 1 : pagination?.current,
    pageSize: pagination?.pageSize,
    ...(field ? { sortField: field, sortOrder } : {}),
  };
};

/** Derives AntD Table's controlled column sortOrder ('ascend'|'descend'|null)
 * from URL-backed params, so arrows stay correct after a bookmark/back-nav
 * restore (defaultSortOrder only applies at mount and won't do this). */
export const antdSortOrder = (params, columnKey) => {
  if (params?.sortField !== columnKey) return null;
  return params?.sortOrder === 'DESC' ? 'descend' : 'ascend';
};

export default useTableQueryParams;
